export type LanguageCode = "de" | "en";

export class Language {

    public onChange = e5.Signal.create<[LanguageCode]>();

    private _current: LanguageCode = "en";
    private _default: LanguageCode = "de";
    private _ordered: LanguageOrder = {
        "de": [
            "de",
            "en"
        ],
        "en": [
            "en",
            "de"
        ]
    };
    private _locales: { [lang: string]: string } = {
        "de": "de_DE",
        "en": "en_US"
    };

    constructor() {
        this._current = document.documentElement.getAttribute("lang") as LanguageCode;

        //Not valid language set? Than use "en".
        if (!this._current || !this._ordered[this._current]) {
            this._current = this._default;
        }

        this.update(false);

        window.addEventListener("popstate", (e) => {
            this.setCurrent(this.getLangFromUrl(), false);
        });
    }

    private getLangFromUrl(url: string = window.location.href): LanguageCode {
        var u = new URL(window.location.href);
        if (u.searchParams.has("lang")) {
            return u.searchParams.get("lang") as LanguageCode;
        } else {
            return this._default;
        }
    }

    public get current(): LanguageCode {
        return this._current;
    }

    public set current(language: LanguageCode) {
        this.setCurrent(language);
    }

    public setCurrent(language: LanguageCode, pushHistory: boolean = true) {
        if (!this._ordered[language]) {
            console.warn("Set unknown language: ", language);
            return;
        }
        if (this._current == language)
            return;
        this._current = language;

        this.update(pushHistory);

        this.onChange.dispatch(language);
    }

    private update(pushHistory: boolean): void {

        var locale = this._locales[this._current];
        document.querySelectorAll(`meta[property="og:locale"]`).forEach((item) => {
            item.setAttribute("content", locale);
        });

        document.documentElement.setAttribute("lang", this._current);
        document.querySelectorAll("[data-trans]").forEach(
            (element: Element) => {
                this.updateElement(element);
            });

        try {
            const url = new URL(window.location.href);
            var changed = false;
            if (this._current === this._default) {
                if (url.searchParams.has("lang")) {
                    changed = true;
                    url.searchParams.delete('lang');
                }
            } else {
                if (url.searchParams.get("lang") !== this._current) {
                    changed = true;
                    url.searchParams.set('lang', this._current);
                }
            }
            if (changed) {
                if (pushHistory) {
                    window.history.pushState({
                        lang: this._current
                    }, null, url.toString());
                } else {
                    window.history.replaceState({
                        lang: this._current
                    }, null, url.toString());
                }
            }
        } catch (error) {

        }
    }

    public translate(translation: Translation, fallback: string = ""): string {
        if (!translation) {
            return fallback;
        }

        if (typeof translation === "string") {
            return translation || fallback;
        }

        var ordered = this._ordered[this.current];
        for (var lang of ordered) {
            if (translation[lang]) {
                return translation[lang];
            }
        }
        return fallback;
    }

    private updateElement(element: Element): void {
        var infos: Translation[] = this.getInfos(element) || [];
        for (var info of infos) {
            var t = this.translate(info);

            if (typeof info === "string") {
                element.textContent = t;
            } else if (info) {
                if (!info.type) {
                    element.textContent = t;
                } else {
                    var types = info.type.split("|");
                    for (var type of types) {
                        if (type === "html") {
                            element.innerHTML = t;
                        }
                        else if (type === "text") {
                            element.textContent = t;
                        }
                        else if (type.indexOf("@") === 0) {
                            element.setAttribute(type.substr(1), t);
                        } else {
                            console.warn("Could not set translation. Unkown type: ", type);
                        }
                    }
                }
            }
        }
    }

    public add(element: Element | Element[], ...infos: Translation[]): void {
        if (!Array.isArray(element)) {
            element = [element];
        }

        for (var el of element) {
            var result: Translation[] = this.getInfos(el) || [];
            for (var info of infos) {
                if (info !== null) {
                    if (!Array.isArray(result)) {
                        console.log("RESULT", el, result);
                    }
                    result.push(info);
                }
            }
            this.setInfos(el, ...infos);
            this.updateElement(el);
        }
    }

    public set(element: Element | Element[], ...infos: Translation[]): void {
        if (!Array.isArray(element)) {
            element = [element];
        }

        for (var el of element) {
            this.clear(el);
            this.add(element, ...infos);
        }
    }

    public type(type: LanguageInfoType, trans: Translation): Translation {
        if (typeof trans === "string") {
            return {
                type: type || "text",
                de: trans
            };
        }
        var c = this.convert(trans);
        c.type = type || "text";
        return c;
    }

    public setType(element: Element | Element[], type: LanguageInfoType, trans: Translation): void {
        this.set(element, this.type(type, trans));
    }

    private convert(t: Translation): TranslationInfo {
        if (typeof t === "string") {
            return {
                de: t,
                en: t
            };
        }
        return e5.object.merge({}, t);
    }

    public clear(element: Element): void {
        this.setInfos(element);
    }

    public isEmpty(trans: Translation): boolean {
        if (!trans) {
            return true;
        }

        if (typeof trans === "string") {
            return !Boolean(trans.trim());
        }

        for (var lang in this._locales) {
            if ((trans as any)[lang]) {
                return false;
            }
        }

        return true;
    }

    private getInfos(element: Element): Translation[] {
        if (!element._language) {
            var d = element.getAttribute("data-trans");
            if (d) {
                try {
                    element._language = JSON.parse(d);
                    element.setAttribute("data-trans", "");
                } catch (error) {
                    console.warn(error);
                }
            }
        }
        return element._language;
    }

    private setInfos(element: Element, ...infos: Translation[]): void {
        if (infos.length === 0) {
            element.removeAttribute("data-trans");
            element._language = null;
            return;
        }
        element.setAttribute("data-trans", "");
        element._language = infos.slice();
    }
}

export interface TranslationInfo {
    type?: LanguageInfoType;
    de?: string;
    en?: string;
}

export type Translation = TranslationInfo | string;

export type LanguageInfoType = "html" | "text" | string;

// export interface LanguageInfo extends TranslationInfo {

// }

export interface LanguageOrder {
    [key: string]: LanguageCode[];
}